// Activity domain helpers — pure functions, no React, no i18n (callers do the
// `t()`), so both the Dashboard's ActivityCard and the Portfolio's RecentActivity
// can share them without either module importing the other (FRONTEND_RULES §6).
//
// Operates on the `Transaction` contract (models/common.py:27-41):
//   { transactionId, date, category, heading, amount?, direction?, status,
//     confirmationNumber?, fundTicker?, detail? }
//
// Categories actually served today are contrib | rebal | div | transfer; the model's
// union also allows dist | benef. Anything unrecognised degrades rather than throws.

// The pending/settled rule is owned by utils/pending — imported, never restated, so
// this feed's grouping and the balance hero's pending figure can't disagree.
import { isPending } from './pending';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * Wire category → icon key. THE single owner of this map: it previously existed in
 * three places (`dashboard.adapters` ACTIVITY_ICON, `portfolio.adapters`
 * ACTIVITY_TYPE and `RecentActivity` TYPES), which is how they drifted apart.
 */
export const ACTIVITY_ICON = {
  contrib: 'contribution',
  match: 'match',
  rebal: 'rebalance',
  div: 'dividend',
  dist: 'dividend',
  benef: 'beneficiary',
  transfer: 'rebalance',
  fees: 'fee',
};

export const activityIcon = (category) => ACTIVITY_ICON[category] || 'contribution';

/**
 * Who caused this transaction. The participant reads a feed of mixed-origin events,
 * and "did I do this or did someone else?" is the question the detail panel answers.
 * Returns a KEY — the component supplies the words.
 *
 * The wire's `origin` wins when present (FM-2718): the backend decides it from the
 * Relius type code, which is the only place a payroll deferral can be told from a
 * match — both arrive as `contrib`, and inferring from the category called both
 * "You initiated this". The inference below is the fallback for a payload without it.
 *
 * @returns {'sponsor'|'participant'|'payroll'|'system'|'fiduciary'}
 */
export const activityOrigin = ({ origin, category, icon } = {}) => {
  if (origin) return origin;
  // An employer match is the sponsor's money even though it arrives alongside a
  // contribution. The live feed has no `match` category, so the icon is the tell.
  if (category === 'match' || icon === 'match') return 'sponsor';
  if (category === 'rebal') return 'fiduciary';
  if (category === 'div' || category === 'dist' || category === 'fees') return 'system';
  if (category === 'contrib' || category === 'transfer' || category === 'benef') return 'participant';
  return 'system';
};

/**
 * Visual weight of a row. A $2 dividend and a $2,000 transfer are not equally
 * important to scan past, so the design splits them at $100.
 * A row with no amount (a rebalance, a beneficiary change) is always minor.
 *
 * @returns {'major'|'minor'}
 */
export const amountTier = (amount) => (Math.abs(num(amount)) >= 100 ? 'major' : 'minor');

/**
 * Money direction for display: 'pos' | 'neg' | 'na'.
 *
 * FEE SAFETY — a fee is money leaving the account and must render as a debit
 * whatever the feed says. Some recordkeeper feeds carry direction in a separate
 * flag rather than in the sign of the value, so a fee can arrive marked `pos`.
 * Showing "+$1.11" for an administration fee is a money error, not a styling one.
 *
 * NOTE the wire's `TxnCategory` union does not currently include `fees`, so this
 * branch is presently unreachable — it is a guard for when that category ships,
 * deliberately kept because the failure mode is a wrong sign on money.
 */
export const activityTone = ({ category, direction, amount } = {}) => {
  if (amount == null) return 'na';
  if (category === 'fees') return 'neg';
  if (direction === 'neg') return 'neg';
  if (direction === 'pos') return 'pos';
  return 'neutral';
};

/**
 * Normalized display rows. Returns DATA — amounts stay numbers and every label is a
 * key or a param, so nothing here needs localizing.
 *
 * @param {Array} transactions  wire `Transaction[]`
 * @param {object} [opts.limit]  cap the feed (the card shows 10)
 * @returns {Array<object>} never null
 */
export const activityRows = (transactions = [], { limit } = {}) => {
  const list = Array.isArray(transactions) ? transactions : [];
  const rows = list.map((t) => {
    const icon = activityIcon(t.category);
    const amount = t.amount == null ? null : num(t.amount);
    const tone = activityTone({ category: t.category, direction: t.direction, amount });
    return {
      id: t.transactionId ?? t.id,
      date: t.date,
      heading: t.heading,
      amount,
      // Signed for display, and the SIGN follows the resolved tone — so a fee
      // marked `pos` upstream still reads as a debit.
      signed: tone === 'neg' ? -Math.abs(num(amount)) : amount,
      // Whether a +/− belongs on the figure at all. `direction: 'na'` with an
      // amount present means the money MOVED without changing the balance — a
      // fund-to-fund transfer. The old adapter defaulted the sign to '+', so
      // ACC-P001-ACME's "VTIAX → VTSAX" transfer read as "+$2,000.00", i.e. a
      // $2,000 credit the participant never received. Unsigned is the truth.
      showSign: tone === 'pos' || tone === 'neg',
      tone,
      icon,
      category: t.category,
      status: t.status,
      tier: amountTier(amount),
      origin: activityOrigin({ origin: t.origin, category: t.category, icon }),
      detail: t.detail ?? null,
      // The fund-level legs behind a rolled-up transaction (FM-2718) — the drill-down,
      // never the primary rows.
      lines: Array.isArray(t.lines) && t.lines.length ? t.lines : null,
      // Carried independently of `detail` — the old adapter used it only as a
      // fallback, so a row with both silently lost the confirmation number.
      confirmationNumber: t.confirmationNumber ?? null,
      // Carried so compact lists (the distribution card's recent fund activity) can name
      // the fund; the live feed always has one, the fixture sometimes doesn't.
      fundTicker: t.fundTicker ?? null,
      linkKind: ['contrib', 'match', 'div', 'dist'].includes(t.category) ? 'receipt' : 'details',
    };
  });
  return typeof limit === 'number' ? rows.slice(0, limit) : rows;
};

/**
 * Filter tabs derived from the categories actually present, so no tab can ever be
 * dead. The card previously hardcoded five, but the served feed only emits
 * contrib/rebal/div/transfer — meaning its Distributions and Beneficiary tabs could
 * only ever show an empty state.
 *
 * Returns `[]` when there is nothing to filter (fewer than two distinct groups),
 * so the caller renders no chip row at all.
 *
 * @returns {Array<{id: string, count: number}>} `id: 'all'` first when non-empty
 */
export const activityTabs = (rows = []) => {
  const groups = new Map();
  rows.forEach((r) => {
    const key = TAB_FOR[r.category] || 'other';
    groups.set(key, (groups.get(key) || 0) + 1);
  });
  if (groups.size < 2) return [];
  return [
    { id: 'all', count: rows.length },
    ...[...groups.entries()].map(([id, count]) => ({ id, count })),
  ];
};

/** Category → filter-tab group. Several categories share a tab on purpose. */
const TAB_FOR = {
  contrib: 'contributions',
  match: 'contributions',
  rebal: 'rebalances',
  transfer: 'transfers',
  div: 'earnings',
  dist: 'earnings',
  benef: 'account',
  fees: 'fees',
};

/** Rows in one tab. `all` passes everything through. */
export const rowsForTab = (rows = [], tab = 'all') =>
  (tab === 'all' ? rows : rows.filter((r) => (TAB_FOR[r.category] || 'other') === tab));

/**
 * Settlement groups for the feed: everything still in flight, then everything
 * settled. The design leads with pending because that is the only part of the feed
 * a participant might still act on — money that hasn't landed yet.
 *
 * The pending/settled line is NOT redrawn here: `isPending` (utils/pending) is the
 * one rule the balance hero, the Portfolio feed and this card all read, so a row
 * that inflates the hero's pending balance can't simultaneously file itself under
 * "completed" here (FM-2098). Anything not in that set is settled — the wire emits
 * only `pending` and `posted`, and an unrecognised status is likelier to be a
 * terminal state than an in-flight one.
 *
 * Empty groups are dropped, so a feed with nothing pending renders one "COMPLETED"
 * heading rather than an empty "PENDING" rule.
 *
 * @returns {Array<{key: 'pending'|'completed', count: number, rows: Array}>}
 */
export const activityGroups = (rows = []) => {
  const pending = [];
  const completed = [];
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    (isPending(r.status) ? pending : completed).push(r);
  });
  return [
    { key: 'pending', count: pending.length, rows: pending },
    { key: 'completed', count: completed.length, rows: completed },
  ].filter((g) => g.count > 0);
};
